/**
 * useGameStream — subscribes to /api/games/{gamePk}/stream via EventSource,
 * applies snapshot + diff events into a typed in-memory game-state object,
 * and reconnects with a freshly-fetched idToken on each retry.
 *
 * Token rotation on reconnect is load-bearing: idToken lifetime is ~1h with
 * SDK auto-refresh; we ask the AuthProvider for a fresh one each attempt.
 *
 * Reducer reuses the same RFC 6902 applier as the backend (./jsonpatch).
 */
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { applyPatch, type PatchOp } from './jsonpatch';

export type GumboSnapshot = Record<string, unknown>;

export type StreamStatus = 'connecting' | 'open' | 'closed' | 'error' | 'historical';

export interface StatcastPitch {
  play_id: string;
  pitch_type?: string | null;
  pitch_name?: string | null;
  start_speed?: number | null;
  spin_rate?: number | null;
  launch_speed?: number | null;
  launch_angle?: number | null;
  bat_speed?: number | null;
  hit_distance?: number | null;
  description?: string | null;
  // Plate-crossing coords in feet. Optional — older fixtures and pitches
  // not fully tracked may omit these and StrikeZone renders gracefully.
  plate_x?: number | null;
  plate_z?: number | null;
  sz_top?: number | null;
  sz_bot?: number | null;
}

export interface StatcastState {
  game_pk?: number;
  pitches: Record<string, StatcastPitch>;
}

export interface GameStreamState {
  snapshot: GumboSnapshot | null;
  status: StreamStatus;
  lastError: { code: string; message: string } | null;
  diffSeq: number;
  statcast: StatcastState;
}

export interface UseGameStreamOptions {
  /** Override the API base URL — defaults to VITE_API_BASE then ''. */
  apiBase?: string;
  /** Disable for tests / when you don't want the EventSource opened. */
  enabled?: boolean;
  /**
   * 'live' (default) opens an SSE stream and applies snapshot+diffs.
   * 'historical' makes one GET to /api/games/{game_pk}/snapshot and stops —
   * no SSE, no reconnect. The game is final; the snapshot is the result.
   */
  mode?: 'live' | 'historical';
}

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');
const MAX_BACKOFF_MS = 8_000;

/**
 * Shared route-param → stream-mode rule for GameView / Rosters / BoxScorePage:
 * 'historical' only when explicitly requested, anything else is live.
 */
export function gameModeFromSearch(searchParams: URLSearchParams): 'live' | 'historical' {
  return searchParams.get('mode') === 'historical' ? 'historical' : 'live';
}

function backoff(attempt: number): number {
  return Math.min(MAX_BACKOFF_MS, 250 * Math.pow(2, attempt));
}

/**
 * Structured console.error for every `setLastError` site so the user can
 * always inspect what actually fired, even if the visible banner gets
 * debounced or dismissed. Filter devtools by `useGameStream:lastError` to
 * see the full history.
 */
function logErrorEvent(
  op: string,
  code: string,
  message: string,
  context: Record<string, unknown> = {},
): void {
  // eslint-disable-next-line no-console
  console.error('useGameStream:lastError', {
    op,
    code,
    message,
    ts: Date.now(),
    ...context,
  });
}

export function useGameStream(gamePk: number | undefined, options: UseGameStreamOptions = {}): GameStreamState {
  const { getIdToken, user } = useAuth();
  const [state, setState] = useState<GameStreamState>({
    snapshot: null,
    status: 'connecting',
    lastError: null,
    diffSeq: 0,
    statcast: { pitches: {} },
  });
  const ref = useRef<{ source: EventSource | null; closed: boolean; attempt: number }>({
    source: null,
    closed: false,
    attempt: 0,
  });

  const enabled = options.enabled !== false && Boolean(gamePk) && Boolean(user);
  const apiBase = options.apiBase ?? API_BASE;
  const mode = options.mode ?? 'live';

  // Historical mode: one-shot snapshot fetch, no SSE.
  useEffect(() => {
    if (!enabled || mode !== 'historical') return undefined;
    let cancelled = false;
    setState((s) => ({ ...s, status: 'connecting', lastError: null }));
    (async () => {
      try {
        const token = await getIdToken(true);
        const r = await fetch(`${apiBase}/api/games/${gamePk}/snapshot`, {
          headers: { authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (!r.ok) {
          const text = await r.text();
          const m = text.match(/ERR_[A-Z0-9]{8}/);
          const code = m?.[0] ?? 'ERR_SNAP';
          const message = `HTTP ${r.status}`;
          logErrorEvent('historical-snapshot', code, message, { gamePk, httpStatus: r.status });
          setState((s) => ({
            ...s,
            status: 'error',
            lastError: { code, message },
          }));
          return;
        }
        const data = (await r.json()) as GumboSnapshot;
        if (cancelled) return;
        setState((s) => ({ ...s, snapshot: data, status: 'historical', lastError: null }));
        // Parallel statcast pull — additive, failure-isolated.
        const scStart = performance.now();
        try {
          const sr = await fetch(`${apiBase}/api/games/${gamePk}/statcast`, {
            headers: { authorization: `Bearer ${token}` },
          });
          const durationMs = Math.round(performance.now() - scStart);
          if (cancelled || !sr.ok) {
            // eslint-disable-next-line no-console
            console.debug('useGameStream:statcast', { op: 'historical-statcast', ok: false, status: sr.status, durationMs, gamePk });
            return;
          }
          const sc = (await sr.json()) as StatcastState;
          if (cancelled) return;
          const pitches = sc && typeof sc === 'object' && sc.pitches ? sc.pitches : {};
          // eslint-disable-next-line no-console
          console.debug('useGameStream:statcast', { op: 'historical-statcast', ok: true, pitchCount: Object.keys(pitches).length, durationMs, gamePk });
          setState((s) => ({ ...s, statcast: { game_pk: sc?.game_pk, pitches } }));
        } catch (e) {
          const durationMs = Math.round(performance.now() - scStart);
          // intentional swallow: statcast is additive — keep historical view alive even if Savant is unreachable
          // eslint-disable-next-line no-console
          console.debug('useGameStream:statcast', { op: 'historical-statcast', ok: false, error: (e as Error).message, durationMs, gamePk });
        }
      } catch (err) {
        if (cancelled) return;
        const message = (err as Error).message;
        logErrorEvent('historical-snapshot', 'ERR_FETCH', message, { gamePk });
        setState((s) => ({
          ...s,
          status: 'error',
          lastError: { code: 'ERR_FETCH', message },
        }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, mode, gamePk, apiBase, getIdToken]);

  useEffect(() => {
    if (!enabled || mode !== 'live') return undefined;
    let cancelled = false;
    const r = ref.current;
    r.closed = false;
    r.attempt = 0;

    async function connect(): Promise<void> {
      if (cancelled || r.closed) return;
      try {
        const token = await getIdToken(true);
        if (cancelled || r.closed) return;
        const url = `${apiBase}/api/games/${gamePk}/stream?token=${encodeURIComponent(token)}`;
        setState((s) => ({ ...s, status: 'connecting' }));
        const es = new EventSource(url);
        r.source = es;

        es.addEventListener('open', () => {
          if (cancelled || r.closed) return;
          r.attempt = 0;
          setState((s) => ({ ...s, status: 'open' }));
        });

        es.addEventListener('snapshot', (e) => {
          if (cancelled || r.closed) return;
          try {
            const data = JSON.parse((e as MessageEvent).data) as GumboSnapshot;
            setState((s) => ({ ...s, snapshot: data, status: 'open', lastError: null }));
          } catch (err) {
            const message = (err as Error).message;
            logErrorEvent('snapshot-parse', 'ERR_PARSE_001', message, { gamePk });
            setState((s) => ({
              ...s,
              lastError: { code: 'ERR_PARSE_001', message },
            }));
          }
        });

        es.addEventListener('diff', (e) => {
          if (cancelled || r.closed) return;
          try {
            const payload = JSON.parse((e as MessageEvent).data) as {
              seq: number;
              timecode: string;
              data: Array<{ diff: PatchOp[]; metaData?: { timeStamp?: string } }>;
            };
            setState((s) => {
              if (!s.snapshot) return s;
              const clone = structuredClone(s.snapshot);
              try {
                for (const entry of payload.data ?? []) {
                  // Real MLB diffPatch occasionally returns redundant shapes
                  // (a full-snapshot reply instead of an ops array, or empty
                  // ops with no metadata). Skip anything that isn't a real
                  // RFC-6902 patch array — better than crashing the reducer.
                  if (Array.isArray(entry?.diff) && entry.diff.length > 0) {
                    applyPatch(clone, entry.diff);
                  }
                }
              } catch (applyErr) {
                // This updater runs during React's render phase, outside the
                // outer try/catch — an inapplicable op (snapshot/diff
                // sequencing race) must not crash the tree. Keep the prior
                // snapshot; the next snapshot/reconnect re-syncs.
                const message = (applyErr as Error).message;
                logErrorEvent('diff-apply', 'ERR_DIFF_APPLY', message, { gamePk, seq: payload.seq });
                return { ...s, lastError: { code: 'ERR_DIFF_APPLY', message } };
              }
              return { ...s, snapshot: clone, diffSeq: payload.seq ?? s.diffSeq + 1 };
            });
          } catch (err) {
            const message = (err as Error).message;
            logErrorEvent('diff-parse', 'ERR_DIFF_001', message, { gamePk });
            setState((s) => ({
              ...s,
              lastError: { code: 'ERR_DIFF_001', message },
            }));
          }
        });

        es.addEventListener('statcast', (e) => {
          if (cancelled || r.closed) return;
          try {
            const data = JSON.parse((e as MessageEvent).data) as StatcastState;
            const pitches = data && typeof data === 'object' && data.pitches ? data.pitches : {};
            setState((s) => ({ ...s, statcast: { game_pk: data?.game_pk, pitches } }));
            // intentional swallow: malformed statcast is additive/optional — keep prior state
          } catch {
            /* statcast is non-fatal */
          }
        });

        es.addEventListener('error', (e) => {
          // Two cases:
          //  1. event with `data` (server-sent application-level error).
          //  2. raw EventSource error (network / 5xx). Treat as transport
          //     fault → close + retry.
          const data = (e as MessageEvent).data;
          if (data) {
            try {
              const parsed = JSON.parse(data) as { code: string; message: string };
              logErrorEvent('sse-app-error', parsed.code, parsed.message, { gamePk });
              setState((s) => ({ ...s, lastError: parsed }));
              return;
            } catch {
              /* fall through to transport retry */
            }
          }
          // Transport-level fault: bounce.
          logErrorEvent('sse-transport', 'ERR_TRANSPORT', 'EventSource error', {
            gamePk,
            attempt: r.attempt,
          });
          es.close();
          r.source = null;
          setState((s) => ({ ...s, status: 'error' }));
          if (!r.closed) {
            const wait = backoff(r.attempt++);
            window.setTimeout(connect, wait);
          }
        });

        es.addEventListener('end', () => {
          r.closed = true;
          es.close();
          setState((s) => ({ ...s, status: 'closed' }));
        });
      } catch (err) {
        if (cancelled || r.closed) return;
        const message = (err as Error).message;
        logErrorEvent('token-fetch', 'ERR_TOKEN_001', message, {
          gamePk,
          attempt: r.attempt,
        });
        setState((s) => ({
          ...s,
          status: 'error',
          lastError: { code: 'ERR_TOKEN_001', message },
        }));
        const wait = backoff(r.attempt++);
        window.setTimeout(connect, wait);
      }
    }

    void connect();
    return () => {
      cancelled = true;
      r.closed = true;
      r.source?.close();
      r.source = null;
    };
    // gamePk + getIdToken referentially stable from useAuth; user gates `enabled`.
  }, [gamePk, enabled, mode, apiBase, getIdToken]);

  return state;
}
